<template>
  <div class="legal-page">
    <nav class="navbar">
      <router-link to="/" class="nav-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <router-link to="/login" class="btn-login">Login</router-link>
    </nav>

    <div class="legal-container">
      <div class="legal-content">
        <h1>Terms of Service</h1>
        <p class="last-updated">Last updated: January 29, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using ResyMaid ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            ResyMaid provides AI-powered resume customization tools that help users tailor their resumes to specific job postings. The Service includes:
          </p>
          <ul>
            <li>Web application at resymaid.com</li>
            <li>Chrome browser extension</li>
            <li>Resume parsing and generation features</li>
            <li>Integration with job posting sites (LinkedIn, Indeed, Glassdoor)</li>
          </ul>
        </section>

        <section>
          <h2>3. Account Registration</h2>
          <p>
            To use certain features of the Service, you must create an account. You agree to:
          </p>
          <ul>
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Notify us immediately of any unauthorized access</li>
            <li>Be responsible for all activities under your account</li>
          </ul>
        </section>

        <section>
          <h2>4. Credits and Payment</h2>
          <p>
            ResyMaid operates on a credit-based system:
          </p>
          <ul>
            <li>Credits are required to generate tailored resumes</li>
            <li>Credits are purchased in advance and do not expire</li>
            <li>All purchases are final and non-refundable</li>
            <li>Prices are subject to change with notice</li>
          </ul>
          <p>
            New users receive 3 free credits upon registration. Additional credits may be earned through our referral program.
          </p>
        </section>

        <section>
          <h2>5. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose</li>
            <li>Submit false, misleading, or fraudulent information in resumes</li>
            <li>Attempt to reverse engineer or access the Service's source code</li>
            <li>Interfere with or disrupt the Service</li>
            <li>Share, sell, or transfer your account to others</li>
            <li>Use automated tools to access the Service without permission</li>
            <li>Violate any applicable laws or regulations</li>
          </ul>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            <strong>Our Content:</strong> The Service, including its design, features, and content, is owned by ResyMaid and protected by intellectual property laws.
          </p>
          <p>
            <strong>Your Content:</strong> You retain ownership of the resumes and information you submit. By using the Service, you grant us a license to process your content solely for the purpose of providing the Service.
          </p>
        </section>

        <section>
          <h2>7. AI-Generated Content</h2>
          <p>
            The Service uses artificial intelligence to generate resume content. You acknowledge that:
          </p>
          <ul>
            <li>AI-generated content should be reviewed for accuracy before use</li>
            <li>You are responsible for verifying and approving all content</li>
            <li>We do not guarantee specific outcomes (interviews, job offers, etc.)</li>
            <li>Generated content may not be perfect and may require editing</li>
          </ul>
        </section>

        <section>
          <h2>8. Third-Party Services</h2>
          <p>
            The Service integrates with third-party platforms (LinkedIn, Indeed, Glassdoor) for job posting data. We are not responsible for the availability, accuracy, or policies of these third-party services.
          </p>
        </section>

        <section>
          <h2>9. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE MAKE NO GUARANTEES REGARDING EMPLOYMENT OUTCOMES.
          </p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, RESYMAID SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
          </p>
          <p>
            OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
          </p>
        </section>

        <section>
          <h2>11. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless ResyMaid from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or infringement of any rights of third parties.
          </p>
        </section>

        <section>
          <h2>12. Termination</h2>
          <p>
            We may suspend or terminate your access to the Service at any time for violation of these Terms or for any other reason at our discretion. Upon termination, your right to use the Service ceases immediately.
          </p>
        </section>

        <section>
          <h2>13. Changes to Terms</h2>
          <p>
            We may modify these Terms at any time. We will notify users of material changes by posting the updated Terms on the Service. Your continued use after changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section>
          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions.
          </p>
        </section>

        <section>
          <h2>15. Contact</h2>
          <p>
            For questions about these Terms, contact us at:
          </p>
          <p class="contact-info">
            <strong>ResyMaid</strong><br>
            Email: <a href="mailto:support@resymaid.com">support@resymaid.com</a>
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import AppLogo from "@/components/AppLogo.vue";

export default {
  name: "TermsOfService",
  components: { AppLogo },
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #0a0a0f;
}

.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-logo {
  text-decoration: none;
}

.logo-img {
  height: 56px;
}

.btn-login {
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s;
}

.btn-login:hover {
  background: rgba(255, 255, 255, 0.15);
}

.legal-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.legal-content {
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 3rem;
}

h1 {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.last-updated {
  color: #71717a;
  font-size: 14px;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

section {
  margin-bottom: 2rem;
}

h2 {
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-top: 2rem;
}

p {
  color: #a1a1aa;
  font-size: 15px;
  line-height: 1.7;
  margin-bottom: 1rem;
}

ul {
  color: #a1a1aa;
  font-size: 15px;
  line-height: 1.7;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
}

li {
  margin-bottom: 0.5rem;
}

strong {
  color: #e4e4e7;
}

a {
  color: #8b5cf6;
  text-decoration: none;
}

a:hover {
  color: #a78bfa;
  text-decoration: underline;
}

.contact-info {
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 8px;
  padding: 1rem;
}

@media (max-width: 640px) {
  .legal-content {
    padding: 1.5rem;
  }
  
  h1 {
    font-size: 24px;
  }
}
</style>
